import { useState } from "react"
import styled from "styled-components"

import { Banner, Skeleton, spacing } from "@minuthq/meatball-ui-react"

import { AiAssistantAddContextDialog } from "src/components/AiAssistant/AiAssistantKnowledge/AiAssistantContextLibrary/AiAssistantAddContextDialog"
import { AiAssistantAddSuggestionDialog } from "src/components/AiAssistant/AiAssistantSuggestions/AiAssistantAddSuggestionDialog"
import { AiAssistantSuggestionCard } from "src/components/AiAssistant/AiAssistantSuggestions/AiAssistantSuggestionCard"
import { Pager } from "src/components/Pager/Pager"
import { useOrganization } from "src/data/organizations/hooks/useOrganization"
import { useFetchPropertyContexts } from "src/data/propertyContext/propertyContextQueries"
import { TPropertyContext } from "src/data/propertyContext/propertyContextTypes"
import { useTranslate } from "src/i18n/useTranslate"
import { Routes } from "src/router/routes"
import { useRouter } from "src/router/useRouter"
import { useSearchParams } from "src/router/useSearchParams"
import { ErrorService } from "src/utils/ErrorService"

export function AiAssistantSuggestions() {
  const { t, langKeys } = useTranslate()

  const { navigate } = useRouter()

  const { org } = useOrganization()

  const [addContextDialog, setAddContextDialog] = useState<{
    open: boolean
    suggestion: TPropertyContext | null
  } | null>(null)

  const [addSuggestionDialog, setAddSuggestionDialog] = useState<{
    open: boolean
    context: TPropertyContext | null
  }>({
    open: false,
    context: null,
  })

  const { searchParams, setSearchParams } = useSearchParams({
    keys: [
      {
        key: "offset",
        type: "number",
      },
    ],
  })

  const limit = 15
  const fetchPropertyContexts = useFetchPropertyContexts({
    orgId: org.id,
    filters: {
      status: "SUGGESTED",
      limit,
      offset: searchParams.offset ?? 0,
    },
  })

  if (fetchPropertyContexts.isLoading) {
    return (
      <LoadingWrapper>
        <Skeleton variant="rect" />
        <Skeleton variant="rect" />
        <Skeleton variant="rect" />
      </LoadingWrapper>
    )
  }

  if (fetchPropertyContexts.isError) {
    return (
      <Banner type="error" fullWidth>
        {t(langKeys.failed_contact_support)}
      </Banner>
    )
  }

  const propertyContexts = fetchPropertyContexts.data.property_contexts

  return (
    <div>
      <SuggestionsWrapper>
        {propertyContexts.map((context) => (
          <AiAssistantSuggestionCard
            key={context.id}
            context={context}
            onAddSuggestion={() => {
              setAddSuggestionDialog({
                open: true,
                context,
              })
            }}
            onDeleteSuggestion={() => {
              alert("Not implemented")
            }}
          />
        ))}
      </SuggestionsWrapper>

      <Pager
        limit={limit}
        offset={searchParams.offset ?? 0}
        setOffset={(offset) => {
          setSearchParams("offset", offset)
        }}
        totalCount={fetchPropertyContexts.data.paging.total_count}
      />

      {addSuggestionDialog.open && addSuggestionDialog.context && (
        <AiAssistantAddSuggestionDialog
          context={addSuggestionDialog.context}
          onClose={() => {
            setAddSuggestionDialog({ open: false, context: null })
          }}
          onConfirm={({ createNewContent, existingContext }) => {
            if (!addSuggestionDialog.context) {
              ErrorService.captureException(
                "No context found when adding suggestion"
              )
              return
            }

            if (createNewContent) {
              setAddContextDialog({
                open: true,
                suggestion: addSuggestionDialog.context,
              })
            }

            if (existingContext) {
              navigate(
                Routes.AiAssistantKnowledgeContent.location({
                  contextId: existingContext.id,
                  suggestionId: addSuggestionDialog.context.id,
                })
              )
            }

            setAddSuggestionDialog({ open: false, context: null })
          }}
        />
      )}

      {addContextDialog?.open && addContextDialog.suggestion && (
        <AiAssistantAddContextDialog
          onClose={() => {
            setAddContextDialog(null)
            setAddSuggestionDialog({
              open: true,
              context: addContextDialog.suggestion,
            })
          }}
          suggestion={addContextDialog.suggestion}
        />
      )}
    </div>
  )
}

const LoadingWrapper = styled.div`
  display: grid;
  gap: ${spacing.S};
`

const SuggestionsWrapper = styled.div`
  display: grid;
  gap: ${spacing.XS};
  margin-bottom: ${spacing.L};
`
